import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ActionButtonHandle,
  ApiFilterOperation,
  Filter,
  FormPanel,
  Params,
  ScrollableSource,
  Sort,
  TableFieldHandle,
} from '@eas/common-web';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { DictionaryObject, EsFile, EsFileRegulation } from '@models';
import { RegulationType } from '@enums';
import { useRegulationFieldColumns } from './regulation-columns';
import { RegulationSectionTable } from './regulation-section-table';
import { RegulationToolbar } from './regulation-toolbar';

const REGULATION_SECTIONS = [
  {
    type: RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES,
    title: (
      <FormattedMessage
        id="ES__REGULATIONS__CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES"
        defaultMessage="Ústavní zákony a mezinárodní smlouvy"
      />
    ),
  },
  {
    type: RegulationType.EUROPEAN_UNION_LAW,
    title: (
      <FormattedMessage
        id="ES__REGULATIONS__EUROPEAN_UNION_LAW"
        defaultMessage="Právo Evropské unie"
      />
    ),
  },
  {
    type: RegulationType.OTHER,
    title: (
      <FormattedMessage id="ES__REGULATIONS__OTHER" defaultMessage="Ostatní" />
    ),
  },
];

const getToolbarLabelFilters = (filters: Filter[] = []) =>
  filters.filter(
    (filter) =>
      filter.field === 'label' &&
      filter.operation === ApiFilterOperation.CONTAINS
  );

const mergeToolbarParams = (
  currentParams: Params,
  params: Params,
  filters: Filter[]
): Params => {
  const currentFilters = (currentParams.filters ?? []).filter(
    (filter) => filter.field !== 'label'
  );

  return {
    ...currentParams,
    ...params,
    size: params.size ?? currentParams.size,
    sort:
      params.sort && params.sort.length > 0 ? params.sort : currentParams.sort,
    filters: [...currentFilters, ...filters],
  };
};

export interface RegulationFieldProps {
  regulationTypes: DictionaryObject[];
  regulationEntityTypes: DictionaryObject[];
  disabled: boolean;
  esFile?: EsFile;
  source?: ScrollableSource<EsFileRegulation>;
  typeSources?: {
    [RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES]?: ScrollableSource<EsFileRegulation>;
    [RegulationType.EUROPEAN_UNION_LAW]?: ScrollableSource<EsFileRegulation>;
    [RegulationType.OTHER]?: ScrollableSource<EsFileRegulation>;
  };
}

export function RegulationField({
  regulationEntityTypes,
  regulationTypes,
  disabled,
  esFile,
  source,
  typeSources,
}: RegulationFieldProps) {
  const columns = useRegulationFieldColumns({ regulationTypes });
  const masterSource = source;

  const constitutionalTableRef = useRef<TableFieldHandle>(null);
  const europeanUnionTableRef = useRef<TableFieldHandle>(null);
  const otherTableRef = useRef<TableFieldHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);

  const tableRefs = useMemo(
    () => ({
      [RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES]:
        constitutionalTableRef,
      [RegulationType.EUROPEAN_UNION_LAW]: europeanUnionTableRef,
      [RegulationType.OTHER]: otherTableRef,
    }),
    []
  );

  const clearTableSelections = useCallback(() => {
    Object.values(tableRefs).forEach((tableRef) => {
      tableRef.current?.setSelectedIndex(undefined);
      tableRef.current?.setSelectedIndexes([]);
    });
  }, [tableRefs]);

  const handleSetSelectedIndexes = useCallback(
    (indexes: number[]) => {
      if (indexes.length === 0) {
        clearTableSelections();
      }

      setSelectedIndexes(indexes);
    },
    [clearTableSelections]
  );

  const updateMasterSelection = useCallback(
    (masterIndexes: number[], selected: boolean) => {
      setSelectedIndexes((current) => {
        if (!selected) {
          return current.filter((index) => !masterIndexes.includes(index));
        }

        return Array.from(new Set([...current, ...masterIndexes]));
      });
    },
    []
  );

  const getMasterIndexById = useCallback(
    (id?: string) => {
      if (!id) {
        return -1;
      }

      return masterSource?.items.findIndex((item) => item.id === id) ?? -1;
    },
    [masterSource?.items]
  );

  const getMasterIndexesByRows = useCallback(
    (rows: EsFileRegulation[]) =>
      rows
        .map((row) => getMasterIndexById(row.id))
        .filter((index) => index !== -1),
    [getMasterIndexById]
  );

  const regulationSections = useMemo(
    () =>
      REGULATION_SECTIONS.map((section) => ({
        ...section,
        source: typeSources?.[section.type],
      })),
    [typeSources]
  );

  const syncLabelFiltersToTypeSources = useCallback(
    (filters: Filter[] = []) => {
      const labelFilters = getToolbarLabelFilters(filters);

      regulationSections.forEach((section) => {
        const typeSource = section.source;

        if (!typeSource) {
          return;
        }

        typeSource.setParams(
          mergeToolbarParams(typeSource.getParams(), {}, labelFilters)
        );
      });
    },
    [regulationSections]
  );

  const synchronizedToolbarSource = useMemo<
    ScrollableSource<EsFileRegulation> | undefined
  >(
    () =>
      masterSource
        ? {
            ...masterSource,
            setParams: (params) => {
              const labelFilters = getToolbarLabelFilters(params.filters);

              masterSource.setParams(
                mergeToolbarParams(
                  masterSource.getParams(),
                  params,
                  labelFilters
                )
              );
              syncLabelFiltersToTypeSources(params.filters);
            },
            reset: () => {
              masterSource.reset();
              regulationSections.forEach((section) => section.source?.reset());
            },
            loadMore: async () => {
              const result = await masterSource.loadMore();

              await Promise.all(
                regulationSections.map((section) => section.source?.loadMore())
              );

              return result;
            },
          }
        : undefined,
    [masterSource, regulationSections, syncLabelFiltersToTypeSources]
  );

  const handleSortChange = useCallback(
    (typeSource: ScrollableSource<EsFileRegulation>, sort: Sort[]) => {
      typeSource.setParams({
        ...typeSource.getParams(),
        sort,
      });
      typeSource.reset();
      typeSource.loadMore();
      setSelectedIndexes([]);
      clearTableSelections();
    },
    [clearTableSelections]
  );

  const refreshRegulationSources = useCallback(async () => {
    const sources = [
      masterSource,
      typeSources?.[
        RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES
      ],
      typeSources?.[RegulationType.EUROPEAN_UNION_LAW],
      typeSources?.[RegulationType.OTHER],
    ].filter(Boolean) as ScrollableSource<EsFileRegulation>[];

    for (const regulationSource of sources) {
      regulationSource.reset();
      await regulationSource.loadMore();
    }

    setSelectedIndexes([]);
    clearTableSelections();
  }, [clearTableSelections, masterSource, typeSources]);

  if (!masterSource) {
    return <> Missing REGULATION_SOURCE </>;
  }

  if (regulationSections.some((section) => !section.source)) {
    return <> Missing REGULATION_TYPE_SOURCE </>;
  }

  return (
    <FormPanel
      label={
        <FormattedMessage
          id="ES__REGULATIONS__PANEL_TITLE"
          defaultMessage="Předpisy"
        />
      }
      summary={<FormTableCount source={masterSource} />}
    >
      <RegulationToolbar
        selectedIndex={selectedIndexes[0]}
        setSelectedIndex={async (index) => {
          handleSetSelectedIndexes(index === undefined ? [] : [index]);
        }}
        selectedIndexes={selectedIndexes}
        setSelectedIndexes={handleSetSelectedIndexes}
        source={synchronizedToolbarSource ?? masterSource}
        ref={editRef}
        regulationEntityTypes={regulationEntityTypes}
        regulationTypes={regulationTypes}
        disabled={disabled}
        esFile={esFile}
        onAfterAction={refreshRegulationSources}
      />
      <div
        style={{
          backgroundColor: '#f7f7f7',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginTop: 8,
          padding: 12,
          width: '100%',
        }}
      >
        {regulationSections.map((section) => (
          <RegulationSectionTable
            key={section.type}
            section={section}
            columns={columns}
            disabled={disabled}
            tableRef={tableRefs[section.type]}
            editRef={editRef}
            regulationEntityTypes={regulationEntityTypes}
            regulationTypes={regulationTypes}
            getMasterIndexById={getMasterIndexById}
            getMasterIndexesByRows={getMasterIndexesByRows}
            updateMasterSelection={updateMasterSelection}
            setSelectedIndexes={setSelectedIndexes}
            clearTableSelections={clearTableSelections}
            handleSortChange={handleSortChange}
          />
        ))}
      </div>
    </FormPanel>
  );
}
